import { useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';

interface FileUploadProps {
  id: string;
  label: string;
  name: string;
  file: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  maxSizeMB?: number;
  hint?: string;
  error?: string;
  requiredMark?: boolean;
  labels: {
    upload: string;
    replace: string;
    remove: string;
    noFile: string;
    tooLarge: string;
    wrongType: string;
  };
}

export default function FileUpload({
  id,
  label,
  name,
  file,
  onChange,
  accept,
  maxSizeMB,
  hint,
  error,
  requiredMark,
  labels,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string | null>(null);

  const handleSelect = (selected: File | null) => {
    setInternalError(null);
    if (!selected) return;

    if (accept) {
      const allowed = accept.split(',').map((s) => s.trim().toLowerCase());
      const ext = `.${selected.name.split('.').pop()?.toLowerCase() ?? ''}`;
      const typeOk = allowed.some((a) => a === ext || selected.type === a.replace('.', ''));
      if (!typeOk) {
        setInternalError(labels.wrongType);
        onChange(null);
        return;
      }
    }
    if (maxSizeMB && selected.size > maxSizeMB * 1024 * 1024) {
      setInternalError(labels.tooLarge.replace('{size}', formatNumber(maxSizeMB)));
      onChange(null);
      return;
    }
    onChange(selected);
  };

  const shownError = error || internalError || undefined;

  return (
    <div className="w-full">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground-800">
        {label}
        {requiredMark ? <span className="text-rose-600"> *</span> : null}
      </label>

      <div
        className={cn(
          'flex flex-wrap items-center gap-2 rounded-md border bg-background-50 px-3 py-2.5',
          shownError ? 'border-rose-300' : 'border-background-300',
        )}
      >
        <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 shrink-0">
          <i className="ri-file-text-line text-lg" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-foreground-700">
          {file ? file.name : <span className="text-foreground-400">{labels.noFile}</span>}
        </span>

        <input
          ref={inputRef}
          id={id}
          name={name}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(e) => handleSelect(e.target.files?.[0] ?? null)}
        />

        {file ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1 rounded-md border border-background-300 px-2.5 h-8 text-xs font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              {labels.replace}
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2.5 h-8 text-xs font-medium text-rose-600 hover:bg-rose-50 cursor-pointer"
            >
              <i className="ri-delete-bin-line" aria-hidden="true" />
              {labels.remove}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1 rounded-md bg-secondary-100 border border-secondary-200 px-3 h-8 text-xs font-medium text-secondary-900 hover:bg-secondary-200 cursor-pointer"
          >
            <i className="ri-upload-2-line" aria-hidden="true" />
            {labels.upload}
          </button>
        )}
      </div>

      {shownError ? (
        <p className="mt-1 text-xs text-rose-600">{shownError}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-foreground-500">{hint}</p>
      ) : null}
    </div>
  );
}